import { createRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { UserPlus, FileText, ClipboardList } from "lucide-react";
import { rootRoute } from "./__root";
import { Card } from "../components/ui/Card/Card";
import { PageHeader } from "../components/ui/PageHeader/PageHeader";
import { LoadingSpinner } from "../components/ui/LoadingSpinner/LoadingSpinner";
import { ErrorFallback } from "../components/ui/ErrorFallback/ErrorFallback";
import { StatusBadge } from "../components/ui/StatusBadge/StatusBadge";
import { useLeadList } from "../hooks/use-leads";
import { useOfferList } from "../hooks/use-offers";
import { useActivityList } from "../hooks/use-activities";
import { usePublicSettings } from "../hooks/usePublicSettings";
import { usePermission } from "../lib/use-permission";
import styles from "./Index.module.css";

export const route = createRoute({
	getParentRoute: () => rootRoute,
	path: "/",
	component: IndexPage,
});

function IndexPage() {
	const { t } = useTranslation();
	const { appName } = usePublicSettings();
	const canReadLeads = usePermission("leads", "read");
	const leadsQuery = useLeadList({ enabled: canReadLeads });
	const canReadOffers = usePermission("offers", "read");
	const sentOffersQuery = useOfferList({ emailSent: true, limit: 1, enabled: canReadOffers });
	const canReadActivities = usePermission("activities", "read");
	const dueActivitiesQuery = useActivityList({
		isCompleted: false,
		limit: 1,
		enabled: canReadActivities,
	});

	if (leadsQuery.isLoading) {
		return <LoadingSpinner />;
	}

	if (leadsQuery.isError) {
		return <ErrorFallback onRetry={() => leadsQuery.refetch()} />;
	}

	const leads = leadsQuery.data?.items ?? [];
	const totalLeads = leadsQuery.data?.total ?? 0;

	return (
		<div>
			<PageHeader title={t("panelTitle", { appName })} />

			<div className={styles.kpiGrid}>
				<Card>
					<div className={styles.kpi}>
						<UserPlus size={24} className={styles.kpiIcon} />
						<div>
							<p className={styles.kpiValue}>{totalLeads}</p>
							<p className={styles.kpiLabel}>{t("totalLeads")}</p>
						</div>
					</div>
				</Card>
				<Card>
					<div className={styles.kpi}>
						<FileText size={24} className={styles.kpiIcon} />
						<div>
							<p className={styles.kpiValue}>
								{canReadOffers ? (sentOffersQuery.data?.total ?? "-") : "-"}
							</p>
							<p className={styles.kpiLabel}>{t("offersSent")}</p>
						</div>
					</div>
				</Card>
				<Card>
					<div className={styles.kpi}>
						<ClipboardList size={24} className={styles.kpiIcon} />
						<div>
							<p className={styles.kpiValue}>
								{canReadActivities ? (dueActivitiesQuery.data?.total ?? "-") : "-"}
							</p>
							<p className={styles.kpiLabel}>{t("dueActivities")}</p>
						</div>
					</div>
				</Card>
			</div>

			<div className={styles.sections}>
				<Card header={t("recentLeads")}>
					{leads.slice(0, 5).map((lead: (typeof leads)[0]) => (
						<div key={lead.id} className={styles.listItem}>
							<div>
								<p className={styles.itemTitle}>
									{lead.firstName} {lead.lastName}
								</p>
								<p className={styles.itemMeta}>{lead.companyName || lead.email || "-"}</p>
							</div>
							<StatusBadge status={lead.status} type="lead" />
						</div>
					))}
					{leads.length === 0 && <p className={styles.empty}>{t("noData")}</p>}
				</Card>
			</div>

			<div className={styles.quickActions}>
				<Link
					to="/leads"
					search={{ page: 1, size: 20, status: "", search: "", createdAtFrom: "", createdAtTo: "" }}
					className={styles.quickAction}
				>
					<UserPlus size={18} />
					{t("createLead")}
				</Link>
				<Link
					to="/activities"
					search={{
						page: 1,
						size: 20,
						type: "",
						search: "",
						createdAtFrom: "",
						createdAtTo: "",
						companyId: undefined,
						personId: undefined,
					}}
					className={styles.quickAction}
				>
					<ClipboardList size={18} />
					{t("createActivity")}
				</Link>
			</div>
		</div>
	);
}
